import { FC, useCallback, useEffect, useRef, useState } from "react"
import { Form, Formik, Field, FormikProps, ErrorMessage } from "formik"
import * as Yup from "yup"
import {
  Copy,
  Check,
  Link2,
  Zap,
  Shield,
  Search,
  RefreshCw,
  Wrench,
  HelpCircle,
  ChevronDown
} from "lucide-react"
import {
  Integration,
  EmbedTroubleshooterResult,
  EmbedTroubleshooterSubmitResponse,
  TroubleshooterFinding
} from "../../../../types"
import { UserDetails } from "../../../../types/user"
import {
  useSubmitTroubleshooterMutation,
  useLazyGetTroubleshooterStatusQuery
} from "../../../../redux/queries"
import { useAppSelector } from "../../../hooks"
import { selectUserDetails } from "../../../../redux/slices/auth"
import Input from "../../form/input"
import { PrimaryBtn, LightBtn } from "../../buttons"
import { buildSupportMailto } from "../helpers"

const troubleshootSchema = Yup.object().shape({
  url: Yup.string().url("Must be a valid URL").required("URL is required"),
  problemDescription: Yup.string().required("Problem description is required")
})

type FormValues = { url: string; problemDescription: string }
type ViewState = "form" | "polling" | "results"

const SEVERITY_BORDER: Record<string, string> = {
  error: "border-l-red-500",
  warning: "border-l-yellow-500",
  info: "border-l-blue-400",
  pass: "border-l-green-500"
}

const FIX_PRIORITY_STYLES: Record<
  string,
  { bg: string; bar: string; badge: string }
> = {
  critical: {
    bg: "bg-red-50",
    bar: "border-l-red-400",
    badge: "bg-red-400 text-white"
  },
  recommended: {
    bg: "bg-amber-50",
    bar: "border-l-amber-400",
    badge: "bg-amber-400 text-white"
  },
  "best-practice": {
    bg: "bg-teal-50",
    bar: "border-l-[#63C7C4]",
    badge: "bg-[#63C7C4] text-white"
  }
}

const CHECKS = [
  "SSL Handshake",
  "Header Integrity",
  "CORS Policy",
  "Script Placement",
  "CDN Latency",
  "Container IDs"
]

const MANUAL_TIPS = [
  "Ensure the URL is publicly accessible or whitelisted in your VPC settings.",
  "Capture the specific 'Correlation-ID' from your browser's network tab if available.",
  "Describe any redirection loops encountered during the auth flow."
]

const NeedExpertHelp: FC<{
  integration: Integration
  user: UserDetails | undefined
  disabled?: boolean
}> = ({ integration, user, disabled }) => {
  const href = buildSupportMailto(
    integration,
    user?.username || user?.login || "",
    user?.email || ""
  )
  return (
    <div className="bg-white rounded shadow-sm p-5">
      <div className="flex items-start gap-3 mb-4">
        <span className="w-9 h-9 rounded-lg bg-violet-50 inline-flex items-center justify-center shrink-0">
          <HelpCircle size={18} className="text-violet-400" />
        </span>
        <div>
          <div className="text-sm font-bold text-neutral-900">
            Need Expert Help?
          </div>
          <div className="text-xs text-neutral-400 mt-0.5">
            Typical response time: 14 mins
          </div>
        </div>
      </div>
      {disabled ? (
        <button
          disabled
          className="w-full h-10 bg-neutral-50 border border-neutral-200 rounded text-[13px] font-semibold text-neutral-400 cursor-not-allowed"
        >
          Open Technical Ticket
        </button>
      ) : (
        <a
          href={href}
          className="flex items-center justify-center w-full h-10 bg-white border border-primary-300 rounded text-[13px] font-semibold text-primary-600 hover:bg-primary-50 transition-colors"
        >
          Open Technical Ticket
        </a>
      )}
    </div>
  )
}

const FindingRow: FC<{ finding: TroubleshooterFinding }> = ({ finding }) => {
  const [open, setOpen] = useState(false)
  return (
    <div
      className={`bg-white rounded-lg shadow-sm overflow-hidden border-l-[3px] ${
        SEVERITY_BORDER[finding.severity] || "border-l-neutral-300"
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3.5 text-left cursor-pointer"
      >
        <div className="min-w-0 mr-3">
          <div className="text-sm font-semibold text-neutral-800">
            {finding.title}
          </div>
          {finding.detail && (
            <div className="text-xs text-neutral-400 mt-0.5 line-clamp-1">
              {finding.detail}
            </div>
          )}
        </div>
        <ChevronDown
          size={16}
          className={`text-neutral-400 transition-transform shrink-0 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2 text-sm text-neutral-600 border-t border-neutral-100">
          {finding.detail && <p className="pt-3">{finding.detail}</p>}
          {finding.suggestion && (
            <p>
              <span className="font-medium text-neutral-700">Suggestion:</span>{" "}
              {finding.suggestion}
            </p>
          )}
          {finding.evidence && (
            <pre className="text-xs bg-neutral-50 p-2.5 rounded overflow-x-auto font-mono">
              {finding.evidence}
            </pre>
          )}
        </div>
      )}
    </div>
  )
}

const FixStepCard: FC<{ priority: string; items: string[] }> = ({
  priority,
  items
}) => {
  const style = FIX_PRIORITY_STYLES[priority] || {
    bg: "bg-neutral-50",
    bar: "border-l-neutral-300",
    badge: "bg-neutral-400 text-white"
  }
  return (
    <div className={`${style.bg} rounded-lg p-4 border-l-[3px] ${style.bar}`}>
      <span
        className={`inline-block text-[10px] font-bold uppercase tracking-wider ${style.badge} px-2.5 py-1 rounded-full mb-3`}
      >
        {priority}
      </span>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li
            key={i}
            className="flex gap-2 text-[13px] text-neutral-700 leading-relaxed"
          >
            <span className="shrink-0 opacity-50">•</span>
            {item}
          </li>
        ))}
      </ul>
    </div>
  )
}

interface Props {
  integration: Integration
}

const getStorageKey = (integrationId: string) =>
  `troubleshooter-${integrationId}`

const formatResultsAsText = (result: EmbedTroubleshooterResult): string => {
  const r = result.results
  const lines: string[] = ["Embed Troubleshooter Report", ""]
  if (result.url) lines.push(`URL: ${result.url}`)
  if (result.problemDescription)
    lines.push(`Problem: ${result.problemDescription}`)
  if (r?.timestamp) lines.push(`Date: ${r.timestamp}`)
  lines.push(`Status: ${result.status}`, "")
  if (r?.summary) {
    lines.push(
      `Overall: ${r.summary.overallStatus} — ${r.summary.errors} error(s), ${r.summary.warnings} warning(s), ${r.summary.passes} passed`,
      ""
    )
  }
  if (r?.diagnosis) lines.push("Diagnosis:", r.diagnosis, "")
  if (r?.fixSteps?.length) {
    lines.push("Fix Steps:")
    r.fixSteps.forEach((step, i) =>
      lines.push(`  ${i + 1}. [${step.priority}] ${step.step}`)
    )
    lines.push("")
  }
  if (r?.findings?.length) {
    lines.push("Findings:")
    r.findings.forEach(f => {
      lines.push(`  [${f.severity}] ${f.title}`, `    ${f.detail}`)
      if (f.suggestion) lines.push(`    Suggestion: ${f.suggestion}`)
      if (f.evidence) lines.push(`    Evidence: ${f.evidence}`)
    })
  }
  return lines.join("\n")
}

const Troubleshoot: FC<Props> = ({ integration }) => {
  const user = useAppSelector(selectUserDetails)
  const [viewState, setViewState] = useState<ViewState>("form")
  const [result, setResult] = useState<EmbedTroubleshooterResult | null>(null)
  const [copied, setCopied] = useState(false)
  const [pollProgress, setPollProgress] = useState(0)
  const [pollStep, setPollStep] = useState(0)
  const [submitTroubleshooter] = useSubmitTroubleshooterMutation()
  const [getTroubleshooterStatus] = useLazyGetTroubleshooterStatusQuery()
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const jobIdRef = useRef<string | null>(null)

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }, [])

  useEffect(() => () => stopPolling(), [stopPolling])

  // Visual progress animation while waiting for real poll result
  useEffect(() => {
    if (viewState !== "polling") {
      setPollProgress(0)
      setPollStep(0)
      return
    }
    const start = Date.now()
    const duration = 30000
    const interval = setInterval(() => {
      const elapsed = Date.now() - start
      setPollProgress(Math.min(95, (elapsed / duration) * 100))
      setPollStep(
        Math.min(
          CHECKS.length - 1,
          Math.floor((elapsed / duration) * CHECKS.length)
        )
      )
    }, 150)
    return () => clearInterval(interval)
  }, [viewState])

  useEffect(() => {
    const storedJobId = localStorage.getItem(getStorageKey(integration.id))
    if (!storedJobId) return
    getTroubleshooterStatus(storedJobId)
      .unwrap()
      .then(res => {
        if (res.status === "complete" || res.status === "error") {
          setResult(res)
          setViewState("results")
        } else {
          pollForResult(storedJobId)
        }
      })
      .catch(() => localStorage.removeItem(getStorageKey(integration.id)))
  }, [integration.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const pollForResult = useCallback(
    (id: string) => {
      jobIdRef.current = id
      setViewState("polling")
      pollingRef.current = setInterval(async () => {
        try {
          const res = await getTroubleshooterStatus(id).unwrap()
          if (res.status === "complete" || res.status === "error") {
            stopPolling()
            setResult(res)
            setViewState("results")
          }
        } catch {
          stopPolling()
          setViewState("results")
        }
      }, 3000)
    },
    [getTroubleshooterStatus, stopPolling]
  )

  const handleSubmit = async (values: FormValues) => {
    try {
      const res = (await submitTroubleshooter(
        values
      ).unwrap()) as EmbedTroubleshooterSubmitResponse
      if (res.id) {
        localStorage.setItem(getStorageKey(integration.id), res.id)
        pollForResult(res.id)
      }
    } catch {
      // stay on form
    }
  }

  const handleRunAgain = () => {
    stopPolling()
    localStorage.removeItem(getStorageKey(integration.id))
    setResult(null)
    setViewState("form")
  }

  const handleCopy = () => {
    if (!result) return
    navigator.clipboard.writeText(formatResultsAsText(result)).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  // ── Form ─────────────────────────────────────────────────────────────────
  if (viewState === "form") {
    return (
      <div className="flex flex-col gap-6">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900 mb-2.5 tracking-tight">
            Troubleshoot &amp; Diagnostic
          </h2>
          <p className="text-sm text-neutral-500 leading-relaxed">
            Identify and resolve installation conflicts within your website and
            BankingBridge embed. Complete the parameters below to initiate an
            analysis.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px] items-start">
          <div className="bg-white rounded shadow-sm p-6">
            <Formik
              initialValues={{
                url: integration.url || "",
                problemDescription: ""
              }}
              validationSchema={troubleshootSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting }: FormikProps<FormValues>) => (
                <Form>
                  <Input
                    label="Target Environment URL"
                    name="url"
                    type="url"
                    placeholder="https://integration.yourdomain.com/endpoint"
                    helpText="Specify the exact location where the installation payload is failing."
                    beforeIcon={
                      <Link2 size={15} className="text-neutral-400" />
                    }
                    labelClassName="text-[11px] font-medium uppercase tracking-wider text-neutral-400"
                  />

                  <div className="mt-5">
                    <div className="flex items-center justify-between mb-1.5">
                      <label
                        htmlFor="problemDescription"
                        className="text-[11px] font-medium uppercase tracking-wider text-neutral-400"
                      >
                        Detailed Issue Description{" "}
                        <span className="text-red-400 normal-case">*</span>
                      </label>
                      <span className="text-[10px] font-semibold uppercase tracking-wider bg-neutral-100 text-neutral-500 px-2.5 py-1 rounded">
                        Required
                      </span>
                    </div>
                    <Field
                      as="textarea"
                      id="problemDescription"
                      name="problemDescription"
                      rows={5}
                      placeholder="Describe the unexpected behavior, error codes, or console logs observed during the handshake..."
                      className="appearance-none block w-full px-3 py-2.5 border border-neutral-200 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 resize-y text-sm leading-relaxed"
                    />
                    <ErrorMessage
                      name="problemDescription"
                      component="p"
                      className="mt-2 text-sm text-red-600"
                    />
                  </div>

                  <div className="flex items-center justify-between mt-5">
                    <div className="flex items-center gap-2 text-[13px] text-neutral-500">
                      <Shield size={15} className="text-neutral-400" />
                      Safe Sandbox Execution
                    </div>
                    <PrimaryBtn type="submit" disabled={isSubmitting}>
                      <Zap size={14} className="mr-1.5" />
                      Run Diagnostic
                    </PrimaryBtn>
                  </div>
                </Form>
              )}
            </Formik>
          </div>

          <NeedExpertHelp integration={integration} user={user} />
        </div>
      </div>
    )
  }

  // ── Polling ───────────────────────────────────────────────────────────────
  if (viewState === "polling") {
    return (
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px] items-start">
        <div className="bg-white rounded shadow-sm overflow-hidden">
          <div className="flex flex-col items-center text-center px-10 pt-10 pb-8">
            {/* Spinning icon */}
            <div className="relative w-16 h-16 mb-5 flex items-center justify-center">
              <div className="absolute inset-0 rounded-xl bg-primary-50 flex items-center justify-center">
                <Search size={28} className="text-primary-600" />
              </div>
              <span className="absolute -inset-1 rounded-[14px] border-2 border-transparent border-t-primary-600 border-r-[#63C7C4] animate-spin" />
            </div>
            <h3 className="text-xl font-bold text-neutral-900 mb-2">
              Running Diagnostic Analysis…
            </h3>
            <p className="text-[13px] text-neutral-500 leading-relaxed max-w-md mb-7">
              This usually takes 20–30 seconds. We&apos;re scanning page
              headers, script placement, and URL configuration.
            </p>
            {/* Progress bar */}
            <div className="w-full max-w-lg h-1.5 bg-neutral-100 rounded-full overflow-hidden mb-5">
              <div
                className="h-full bg-gradient-to-r from-primary-600 to-[#63C7C4] rounded-full transition-all duration-500"
                style={{ width: `${pollProgress}%` }}
              />
            </div>
            {/* Check pills */}
            <div className="flex flex-wrap justify-center gap-2">
              {CHECKS.map((c, i) => (
                <span
                  key={c}
                  className={[
                    "inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1.5 rounded-full border",
                    i < pollStep
                      ? "bg-green-50 text-green-700 border-green-200"
                      : i === pollStep
                      ? "bg-primary-50 text-primary-600 border-primary-200"
                      : "bg-neutral-50 text-neutral-400 border-transparent"
                  ].join(" ")}
                >
                  <span
                    className={[
                      "w-1.5 h-1.5 rounded-full",
                      i < pollStep
                        ? "bg-green-500"
                        : i === pollStep
                        ? "bg-primary-500 animate-pulse"
                        : "bg-neutral-300"
                    ].join(" ")}
                  />
                  {c}
                </span>
              ))}
            </div>
          </div>

          {/* Dark manual panel */}
          <div className="bg-[#281D57] p-6">
            <h4 className="text-[15px] font-bold text-white mb-2.5">
              Diagnostic Manual
            </h4>
            <p className="text-[13px] text-white/70 leading-relaxed mb-4">
              Our diagnostic engine performs a 12-point inspection including SSL
              handshake verification, header integrity, and CORS policy
              alignment.
            </p>
            {MANUAL_TIPS.map((tip, i) => (
              <div key={i} className="flex gap-2.5 mb-2.5 last:mb-0">
                <span className="w-[22px] h-[22px] rounded-full bg-[#63C7C4]/20 text-[#63C7C4] text-[11px] font-bold inline-flex items-center justify-center shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <span className="text-[12.5px] text-white/80 leading-relaxed">
                  {tip}
                </span>
              </div>
            ))}
          </div>
        </div>

        <NeedExpertHelp integration={integration} user={user} disabled />
      </div>
    )
  }

  // ── Results ───────────────────────────────────────────────────────────────
  const results = result?.results
  const summary = results?.summary

  if (result?.status === "error" && !results) {
    return (
      <div className="bg-white rounded shadow-sm p-6">
        <div className="bg-red-50 border border-red-200 rounded p-4 mb-4">
          <p className="text-red-800 text-sm font-medium">
            Diagnostics failed. Please try again.
          </p>
        </div>
        <LightBtn onClick={handleRunAgain}>Run Again</LightBtn>
      </div>
    )
  }

  const fixGroups = results?.fixSteps
    ? ["critical", "recommended", "best-practice"].reduce<
        Record<string, string[]>
      >((acc, priority) => {
        const items = results
          .fixSteps!.filter(s => s.priority === priority)
          .map(s => s.step)
        if (items.length) acc[priority] = items
        return acc
      }, {})
    : {}

  const overallStatusClass =
    summary?.overallStatus === "pass"
      ? "text-green-700"
      : summary?.overallStatus === "error"
      ? "text-red-600"
      : "text-amber-500"

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400 mb-2">
            Workflow Diagnostic
          </div>
          <h2 className="text-2xl font-bold text-neutral-900 tracking-tight">
            {integration.title}
          </h2>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <LightBtn onClick={handleCopy}>
            {copied ? (
              <Check size={13} className="mr-1.5" />
            ) : (
              <Copy size={13} className="mr-1.5" />
            )}
            {copied ? "Copied" : "Copy Results"}
          </LightBtn>
          <LightBtn onClick={handleRunAgain}>
            <RefreshCw size={13} className="mr-1.5" />
            Run Again
          </LightBtn>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[
            {
              label: "Diagnostic Status",
              value: summary.overallStatus,
              cls: overallStatusClass
            },
            {
              label: "Errors",
              value: summary.errors,
              cls: summary.errors > 0 ? "text-red-600" : "text-neutral-900"
            },
            {
              label: "Warnings",
              value: summary.warnings,
              cls: summary.warnings > 0 ? "text-amber-500" : "text-neutral-900"
            },
            { label: "Passed", value: summary.passes, cls: "text-green-700" }
          ].map(card => (
            <div
              key={card.label}
              className="bg-white rounded shadow-sm px-5 py-4"
            >
              <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 mb-2">
                {card.label}
              </div>
              <div className={`text-2xl font-extrabold ${card.cls}`}>
                {card.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Diagnosis */}
      {results?.diagnosis && (
        <p className="text-sm text-neutral-700 leading-relaxed">
          {results.diagnosis}
        </p>
      )}

      {/* Fix steps */}
      {Object.keys(fixGroups).length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3.5">
            <Wrench size={15} className="text-neutral-700" />
            <h3 className="text-base font-bold text-neutral-900">Fix Steps</h3>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {Object.entries(fixGroups).map(([priority, items]) => (
              <FixStepCard key={priority} priority={priority} items={items} />
            ))}
          </div>
        </div>
      )}

      {/* Findings */}
      {results?.findings && results.findings.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3.5">
            <div className="flex items-center gap-2">
              <Search size={15} className="text-neutral-700" />
              <h3 className="text-base font-bold text-neutral-900">Findings</h3>
            </div>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
              {results.findings.length} items
            </span>
          </div>
          <div className="flex flex-col gap-2">
            {results.findings.map(
              (finding: TroubleshooterFinding, i: number) => (
                <FindingRow key={finding.checkId || i} finding={finding} />
              )
            )}
          </div>
        </div>
      )}

      {/* Metadata */}
      {results?.metadata && Object.keys(results.metadata).length > 0 && (
        <div className="text-xs text-neutral-400">
          {results.metadata.cms && <span>CMS: {results.metadata.cms}</span>}
          {results.metadata.cms && results.metadata.platform && (
            <span> | </span>
          )}
          {results.metadata.platform && (
            <span>Platform: {results.metadata.platform}</span>
          )}
        </div>
      )}
    </div>
  )
}

export default Troubleshoot
